import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import * as alertifyjs from 'alertifyjs';
import Swal from 'sweetalert2';
import { RbacAdminService } from 'src/app/services/rbac-admin/rbac-admin.service';
import {
  AL_FINAL, AVISO_ORGANIZAR, alternarAbierta, ayudaEliminar, errorDeApi, esEditado, etiquetaOrigenCasilla, filasDeGrupo, grupoAbierto, huecoDeSoltar,
  llaveFila, ordenarCasillas, pantallasVisibles, cambiosDePlan, planArrastrar, planMoverCasilla, planMoverFila, planSubirBajar,
  puedeEliminarGrupo, puedeEliminarPantalla, seccionesDePantalla, seccionesFiltradas, validarNombre
} from './organizar-matriz.logica';

/**
 * Pestaña «Organizar la matriz» de Roles y Permisos: renombrar y reordenar lo que ya existe (módulos, filas y casillas),
 * mover filas a otro módulo o grupo y crear módulos y grupos. Solo presentación: nadie gana ni pierde permisos.
 * Cada cambio se guarda al confirmar y la lista se vuelve a leer. La lógica pura está en organizar-matriz.logica.ts.
 */
@Component({
  selector: 'app-organizar-matriz',
  templateUrl: './organizar-matriz.component.html'
})
export class OrganizarMatrizComponent implements OnInit {

  /** Se guardó un cambio: el padre recarga la matriz. */
  @Output() guardado = new EventEmitter<void>();

  readonly aviso = AVISO_ORGANIZAR;
  readonly alFinal = AL_FINAL;
  readonly esEditado = esEditado;
  readonly etiquetaOrigenCasilla = etiquetaOrigenCasilla;
  readonly ordenarCasillas = ordenarCasillas;
  readonly llaveFila = llaveFila;
  readonly ayudaEliminar = ayudaEliminar;
  readonly puedeEliminarGrupo = puedeEliminarGrupo;
  readonly puedeEliminarPantalla = puedeEliminarPantalla;

  cargando = true;
  errorCarga = '';
  ocupado = false;
  pantallas: any[] = [];
  elegida = '';

  /** Lo que se está renombrando en línea: tipo (modulo | fila | casilla), clave y texto. */
  edicion: { tipo: string; clave: string; valor: string } | null = null;
  errorEdicion = '';

  /** El texto del campo de renombrar (la plantilla no puede escribir en `edicion` mientras puede ser null). */
  get textoEdicion(): string { return this.edicion ? this.edicion.valor : ''; }
  set textoEdicion(v: string) { if (this.edicion) this.edicion.valor = v; }

  /** «Mover a…» abierto: la fila y el destino. */
  moviendo: { fila: any; pantalla: string; grupo: string; despuesDe: string } | null = null;
  errorMover = '';

  /** «+ Nuevo módulo» y «+ Nuevo grupo». */
  nuevoModulo: { nombre: string; despuesDe: string } | null = null;
  nuevoGrupo: { nombre: string } | null = null;
  errorNuevo = '';

  /** Plegado: una sola fila abierta (por llave), grupos abiertos o cerrados según lo elegido, y el buscador de filas. */
  filaAbierta = '';
  gruposAbiertos: { [id: string]: boolean } = {};
  buscar = '';

  /** Arrastrar y soltar (HTML5 nativo). `hueco` = dónde caería: 0 = antes de la primera, n = al final. */
  arrastre: { tipo: 'modulo' | 'fila'; clave: string; grupo: string } | null = null;
  hueco: number | null = null;
  errorArrastre = '';

  /** Confirmación de eliminar, dentro de la página. */
  eliminando: { tipo: 'grupo' | 'modulo'; id: string; nombre: string } | null = null;
  errorEliminar = '';

  constructor(private rbacService: RbacAdminService) { }

  ngOnInit() {
    this.cargar();
  }

  async cargar() {
    try {
      const resp = await this.rbacService.getPantallas();
      this.pantallas = pantallasVisibles((resp?.data || []).slice().sort((a: any, b: any) => (a.orden || 0) - (b.orden || 0)));
      this.errorCarga = '';
      if (!this.pantallas.some((p) => p.id === this.elegida)) this.elegida = this.pantallas[0]?.id || '';
    } catch (error: any) {
      this.errorCarga = errorDeApi(error, 'leer los módulos de la matriz');
    } finally {
      this.cargando = false;
    }
  }

  get pantalla(): any {
    return this.pantallas.find((p) => p.id === this.elegida) || null;
  }

  get secciones(): any[] {
    return seccionesDePantalla(this.pantalla);
  }

  /** Las secciones con el buscador aplicado. */
  get seccionesVistas(): any[] {
    return seccionesFiltradas(this.secciones, this.buscar);
  }

  get filtrando(): boolean {
    return this.buscar.trim().length > 0;
  }

  elegir(id: string) {
    this.elegida = id;
    this.filaAbierta = '';
    this.gruposAbiertos = {};
    this.buscar = '';
    this.cancelar();
  }

  // ── Plegar y desplegar ──
  alternarFila(f: any) {
    this.filaAbierta = alternarAbierta(this.filaAbierta, llaveFila(f));
    this.cancelar();
  }

  /** Con el buscador activo los grupos con coincidencias se ven abiertos. */
  seccionAbierta(s: any): boolean {
    return this.filtrando || grupoAbierto(this.gruposAbiertos, s.id, this.secciones.length);
  }

  alternarSeccion(s: any) {
    this.gruposAbiertos = { ...this.gruposAbiertos, [s.id]: !grupoAbierto(this.gruposAbiertos, s.id, this.secciones.length) };
  }

  /** Teclado: Enter o espacio sobre una línea plegable. */
  teclaPlegar(e: Event, accion: () => void) {
    e.preventDefault();
    accion();
  }

  /** Los módulos a la vista (sin «Otros» vacío). */
  private get idsModulos(): string[] {
    return this.pantallas.map((p) => p.id);
  }

  // ── Arrastrar y soltar ──
  /** Empieza a arrastrar desde el asa: la imagen que se arrastra es toda la línea. */
  inicioArrastre(e: DragEvent, tipo: 'modulo' | 'fila', clave: string, grupo: string) {
    if (this.ocupado || (tipo === 'fila' && this.filtrando)) { e.preventDefault(); return; }
    this.arrastre = { tipo, clave, grupo };
    this.hueco = null;
    this.errorArrastre = '';
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', clave);
      const linea = (e.target as HTMLElement).closest('[data-arrastrable]');
      if (linea) e.dataTransfer.setDragImage(linea as HTMLElement, 16, 16);
    }
  }

  finArrastre() {
    this.arrastre = null;
    this.hueco = null;
  }

  /** Pasar por encima de una línea: se marca el hueco según la mitad (arriba o abajo). */
  sobreLinea(e: DragEvent, tipo: 'modulo' | 'fila', grupo: string, indice: number) {
    const a = this.arrastre;
    if (!a || a.tipo !== tipo || a.grupo !== grupo) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    const caja = (e.currentTarget as HTMLElement).getBoundingClientRect();
    this.hueco = huecoDeSoltar(indice, e.clientY > caja.top + caja.height / 2);
  }

  /** ¿Se marca el borde de arriba (o el de abajo de la última) de esta línea como lugar de caída? */
  marcaArriba(tipo: 'modulo' | 'fila', grupo: string, indice: number): boolean {
    const a = this.arrastre;
    return !!a && a.tipo === tipo && a.grupo === grupo && this.hueco === indice && this.cambiaria(a, indice);
  }

  marcaAbajo(tipo: 'modulo' | 'fila', grupo: string, indice: number, total: number): boolean {
    const a = this.arrastre;
    return !!a && a.tipo === tipo && a.grupo === grupo && indice === total - 1 && this.hueco === total && this.cambiaria(a, total);
  }

  /** Clase del indicador de caída: una línea azul arriba o, en la última, abajo. */
  marca(tipo: 'modulo' | 'fila', grupo: string, indice: number, total: number): string {
    if (this.marcaArriba(tipo, grupo, indice)) return 'shadow-[inset_0_2px_0_#3b82f6]';
    if (this.marcaAbajo(tipo, grupo, indice, total)) return 'shadow-[inset_0_-2px_0_#3b82f6]';
    return '';
  }

  private cambiaria(a: { tipo: string; clave: string; grupo: string }, hueco: number): boolean {
    return !!planArrastrar(this.clavesDeArrastre(a), a.clave, hueco);
  }

  private clavesDeArrastre(a: { tipo: string; grupo: string }): string[] {
    return a.tipo === 'modulo' ? this.idsModulos : filasDeGrupo(this.pantalla, a.grupo).map((f: any) => f.nombre);
  }

  async soltar(e: DragEvent) {
    e.preventDefault();
    const a = this.arrastre;
    const hueco = this.hueco;
    this.finArrastre();
    if (!a || hueco === null || this.ocupado) return;
    const pasos = planArrastrar(this.clavesDeArrastre(a), a.clave, hueco);
    if (!pasos) return;
    const enFila = (nombre: string) => filasDeGrupo(this.pantalla, a.grupo).find((f: any) => f.nombre === nombre);
    await this.guardar(async () => {
      for (const p of pasos) {
        if (a.tipo === 'modulo') {
          await this.rbacService.editarPantalla(p.mover, cambiosDePlan(p));
        } else {
          const f = enFila(p.mover);
          if (!f) throw { status: 404 };
          await this.rbacService.editarFila(f.module, f.submodule, cambiosDePlan(p));
        }
      }
    }, a.tipo === 'modulo' ? 'Módulo movido' : 'Fila movida', a.tipo === 'modulo' ? 'mover el módulo' : 'mover la fila',
      (msg) => { this.errorArrastre = msg; });
  }

  // ── Eliminar grupo o módulo ──
  pedirEliminar(tipo: 'grupo' | 'modulo', id: string, nombre: string) {
    this.cancelar();
    this.eliminando = { tipo, id, nombre };
  }

  async confirmarEliminar() {
    const e = this.eliminando;
    const p = this.pantalla;
    if (!e || !p) return;
    await this.guardar(
      () => e.tipo === 'grupo' ? this.rbacService.eliminarGrupo(p.id, e.id) : this.rbacService.eliminarPantalla(e.id),
      e.tipo === 'grupo' ? 'Grupo eliminado' : 'Módulo eliminado',
      e.tipo === 'grupo' ? 'eliminar el grupo' : 'eliminar el módulo',
      (msg) => { this.errorEliminar = msg; });
  }

  /** Eliminar una fila o casilla creada desde la interfaz: pregunta en simple qué se borra y recarga la matriz. */
  async eliminarCreada(tipo: 'fila' | 'casilla', nombre: string, accion: () => Promise<any>) {
    if (this.ocupado) return;
    const r = await Swal.fire({
      title: tipo === 'fila' ? '¿Eliminar la fila?' : '¿Eliminar la casilla?',
      text: `Se elimina «${nombre}» de la matriz y deja de esconderse en la pantalla. Esto no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });
    if (!r.isConfirmed) return;
    await this.guardar(accion, tipo === 'fila' ? 'Fila eliminada' : 'Casilla eliminada', tipo === 'fila' ? 'eliminar la fila' : 'eliminar la casilla');
  }

  eliminarFilaCreada(f: any) {
    return this.eliminarCreada('fila', f.nombre, () => this.rbacService.eliminarFila(f.module, f.submodule));
  }

  eliminarCasillaCreada(c: any) {
    return this.eliminarCreada('casilla', c.nombre, () => this.rbacService.eliminarCasilla(c.code));
  }

  cancelar() {
    this.edicion = null;
    this.errorEdicion = '';
    this.moviendo = null;
    this.errorMover = '';
    this.nuevoModulo = null;
    this.nuevoGrupo = null;
    this.errorNuevo = '';
    this.eliminando = null;
    this.errorEliminar = '';
    this.errorArrastre = '';
  }

  /** Corre un cambio: guarda, avisa, vuelve a leer la lista y le dice al padre. Devuelve true si salió bien. */
  private async guardar(accion: () => Promise<any>, hecho: string, queHacia: string, alFallar?: (msg: string) => void): Promise<boolean> {
    if (this.ocupado) return false;
    this.ocupado = true;
    try {
      await accion();
      alertifyjs.success(hecho);
      this.cancelar();
      await this.cargar();
      this.guardado.emit();
      return true;
    } catch (error: any) {
      const msg = errorDeApi(error, queHacia);
      if (alFallar) alFallar(msg); else alertifyjs.error(msg);
      if (error?.status === 404) await this.cargar();
      return false;
    } finally {
      this.ocupado = false;
    }
  }

  // ── Renombrar en línea ──
  empezarRenombrar(tipo: string, clave: string, valor: string) {
    this.cancelar();
    this.edicion = { tipo, clave, valor };
  }

  renombrando(tipo: string, clave: string): boolean {
    return !!this.edicion && this.edicion.tipo === tipo && this.edicion.clave === clave;
  }

  async confirmarRenombrar(fila?: any, casilla?: any) {
    const e = this.edicion;
    if (!e) return;
    const nuevo = e.valor.trim().replace(/\s+/g, ' ');
    const fallo = (m: string) => { this.errorEdicion = m; };
    if (e.tipo === 'modulo') {
      const p = this.pantallas.find((x) => x.id === e.clave);
      const err = validarNombre(nuevo, this.pantallas.map((x) => x.nombre), p?.nombre);
      if (err) return fallo(err);
      if (nuevo === p.nombre) return this.cancelar();
      await this.guardar(() => this.rbacService.editarPantalla(e.clave, { nombre: nuevo }), 'Módulo renombrado', 'cambiar el nombre del módulo', fallo);
    } else if (e.tipo === 'fila' && fila) {
      const hermanas = filasDeGrupo(this.pantalla, fila.grupo).filter((f: any) => llaveFila(f) !== llaveFila(fila)).map((f: any) => f.nombre);
      const err = validarNombre(nuevo, hermanas, fila.nombre);
      if (err) return fallo(err);
      if (nuevo === fila.nombre) return this.cancelar();
      await this.guardar(() => this.rbacService.editarFila(fila.module, fila.submodule, { fila: nuevo }), 'Fila renombrada', 'cambiar el nombre de la fila', fallo);
    } else if (e.tipo === 'casilla' && fila && casilla) {
      const otras = (fila.casillas || []).filter((c: any) => c.code !== casilla.code).map((c: any) => c.nombre);
      const err = validarNombre(nuevo, otras, casilla.nombre);
      if (err) return fallo(err);
      if (nuevo === casilla.nombre) return this.cancelar();
      await this.guardar(() => this.rbacService.editarCasilla(casilla.code, { casilla: nuevo }), 'Casilla renombrada', 'cambiar el nombre de la casilla', fallo);
    }
  }

  // ── Subir y bajar ──
  async moverModulo(id: string, dir: 'subir' | 'bajar') {
    const plan = planSubirBajar(this.pantallas.map((p) => p.id), id, dir);
    if (!plan) return;
    await this.guardar(() => this.rbacService.editarPantalla(plan.mover, cambiosDePlan(plan)), 'Módulo movido', 'mover el módulo');
  }

  async moverFila(fila: any, dir: 'subir' | 'bajar') {
    const nombres = filasDeGrupo(this.pantalla, fila.grupo).map((f: any) => f.nombre);
    const plan = planSubirBajar(nombres, fila.nombre, dir);
    if (!plan) return;
    const quien = filasDeGrupo(this.pantalla, fila.grupo).find((f: any) => f.nombre === plan.mover);
    if (!quien) return;
    await this.guardar(() => this.rbacService.editarFila(quien.module, quien.submodule, cambiosDePlan(plan)), 'Fila movida', 'mover la fila');
  }

  puedeSubirModulo(id: string): boolean {
    return !!planSubirBajar(this.pantallas.map((p) => p.id), id, 'subir');
  }

  puedeBajarModulo(id: string): boolean {
    return !!planSubirBajar(this.pantallas.map((p) => p.id), id, 'bajar');
  }

  puedeSubirFila(fila: any): boolean {
    return !!planSubirBajar(filasDeGrupo(this.pantalla, fila.grupo).map((f: any) => f.nombre), fila.nombre, 'subir');
  }

  puedeBajarFila(fila: any): boolean {
    return !!planSubirBajar(filasDeGrupo(this.pantalla, fila.grupo).map((f: any) => f.nombre), fila.nombre, 'bajar');
  }

  // ── Casillas ──
  async moverCasilla(fila: any, casilla: any, dir: 'izquierda' | 'derecha') {
    const plan = planMoverCasilla(fila.casillas, casilla.code, dir);
    if (!plan) return;
    await this.guardar(() => this.rbacService.editarCasilla(plan.code, { mover: plan.mover as 'antes' | 'despues' }), 'Casilla movida', 'mover la casilla');
  }

  puedeMoverCasilla(fila: any, casilla: any, dir: 'izquierda' | 'derecha'): boolean {
    return !!planMoverCasilla(fila.casillas, casilla.code, dir);
  }

  // ── Mover a… ──
  abrirMover(fila: any) {
    this.cancelar();
    this.moviendo = { fila, pantalla: this.elegida, grupo: fila.grupo || '', despuesDe: AL_FINAL };
  }

  get pantallaDestino(): any {
    return this.moviendo ? this.pantallas.find((p) => p.id === this.moviendo!.pantalla) || null : null;
  }

  /** Filas del destino después de las cuales se puede poner la fila (todas menos ella misma). */
  get filasDestino(): any[] {
    if (!this.moviendo) return [];
    return filasDeGrupo(this.pantallaDestino, this.moviendo.grupo).filter((f: any) => llaveFila(f) !== llaveFila(this.moviendo!.fila));
  }

  alCambiarModuloDestino() {
    if (!this.moviendo) return;
    this.moviendo.grupo = '';
    this.moviendo.despuesDe = AL_FINAL;
    this.errorMover = '';
  }

  alCambiarGrupoDestino() {
    if (!this.moviendo) return;
    this.moviendo.despuesDe = AL_FINAL;
    this.errorMover = '';
  }

  async confirmarMover() {
    const m = this.moviendo;
    if (!m) return;
    const plan = planMoverFila(this.pantallas, m.fila, this.elegida, { pantalla: m.pantalla, grupo: m.grupo, despuesDe: m.despuesDe });
    if (plan.error) { this.errorMover = plan.error; return; }
    await this.guardar(
      () => this.rbacService.editarFila(m.fila.module, m.fila.submodule, plan.cambios),
      'Fila movida', 'mover la fila', (msg) => { this.errorMover = msg; });
  }

  // ── Módulo y grupo nuevos ──
  abrirNuevoModulo() {
    this.cancelar();
    this.nuevoModulo = { nombre: '', despuesDe: AL_FINAL };
  }

  async confirmarNuevoModulo() {
    const n = this.nuevoModulo;
    if (!n) return;
    const err = validarNombre(n.nombre, this.pantallas.map((p) => p.nombre), null);
    if (err) { this.errorNuevo = err; return; }
    const nombre = n.nombre.trim().replace(/\s+/g, ' ');
    const antes = this.pantallas.map((p) => p.id);
    const ok = await this.guardar(
      () => this.rbacService.crearPantalla({ nombre, despuesDe: n.despuesDe === AL_FINAL ? null : n.despuesDe }),
      'Módulo creado', 'crear el módulo', (msg) => { this.errorNuevo = msg; });
    if (ok) {
      const nuevo = this.pantallas.find((p) => !antes.includes(p.id));
      if (nuevo) this.elegida = nuevo.id;
    }
  }

  abrirNuevoGrupo() {
    this.cancelar();
    this.nuevoGrupo = { nombre: '' };
  }

  async confirmarNuevoGrupo() {
    const n = this.nuevoGrupo;
    const p = this.pantalla;
    if (!n || !p) return;
    const err = validarNombre(n.nombre, (p.grupos || []).map((g: any) => g.nombre), null);
    if (err) { this.errorNuevo = err; return; }
    await this.guardar(
      () => this.rbacService.crearGrupo(p.id, { nombre: n.nombre.trim().replace(/\s+/g, ' ') }),
      'Grupo creado', 'crear el grupo', (msg) => { this.errorNuevo = msg; });
  }
}
